import type { TFunction } from "i18next";

export interface CityHallFormatters {
  formatDate: (dateString: string | null | undefined) => string;
  formatTimeRemaining: (termEndAt: string | null | undefined) => string;
  formatElectionTimeRemaining: (electionEndAt: string | null | undefined) => string;
  formatNextElectionTimeRemaining: (nextElectionStartDate: string | null | undefined) => string;
}

export interface CreateCityHallFormattersParams {
  /** i18next instance — `language` is read at call time, as in the original inline closures. */
  i18n: { language: string };
  t: TFunction;
  /** Ticking clock state; only `formatNextElectionTimeRemaining` depends on it. */
  currentTime: Date;
}

/**
 * Plain factory (deliberately NOT a hook): these are the four date/countdown
 * closures that used to live inline in CityHallScreen. Keeping them hook-free
 * means extracting them cannot perturb hook or effect ordering in the screen.
 */
export function createCityHallFormatters({
  i18n,
  t,
  currentTime,
}: CreateCityHallFormattersParams): CityHallFormatters {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      // Використовуємо поточну мову з i18next замість локалі браузера
      // Мапінг мов i18next до локалей для toLocaleString
      const localeMap: { [key: string]: string } = {
        'uk': 'uk-UA',
        'en': 'en-US',
        'de': 'de-DE',
        'ru': 'ru-RU',
        'pl': 'pl-PL',
        'es': 'es-ES',
        'fr': 'fr-FR',
        'it': 'it-IT',
        'pt': 'pt-PT',
        'pt-BR': 'pt-BR',
      };
      const locale = localeMap[i18n.language] || i18n.language || 'en-US';
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return dateString;
    }
  };

  const formatTimeRemaining = (termEndAt: string | null | undefined) => {
    if (!termEndAt) return '';
    try {
      const endDate = new Date(termEndAt);
      const now = new Date();
      const diff = endDate.getTime() - now.getTime();

      if (diff <= 0) {
        return t("Term has ended");
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      const parts: string[] = [];
      if (days > 0) {
        parts.push(`${days} ${days === 1 ? "day" : "days"}`);
      }
      if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
      }
      if (minutes > 0 || parts.length === 0) {
        parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      }

      return parts.join(', ');
    } catch {
      return '';
    }
  };

  const formatElectionTimeRemaining = (electionEndAt: string | null | undefined) => {
    if (!electionEndAt) return '';
    try {
      const endDate = new Date(electionEndAt);
      const now = new Date();
      const diff = endDate.getTime() - now.getTime();

      if (diff <= 0) {
        return t("Election has ended");
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      const parts: string[] = [];
      if (days > 0) {
        parts.push(`${days} ${days === 1 ? "day" : "days"}`);
      }
      if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
      }
      if (minutes > 0 || parts.length === 0) {
        parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      }

      return parts.join(', ');
    } catch {
      return '';
    }
  };

  const formatNextElectionTimeRemaining = (nextElectionStartDate: string | null | undefined) => {
    if (!nextElectionStartDate) return '';
    try {
      const startDate = new Date(nextElectionStartDate);
      const diff = startDate.getTime() - currentTime.getTime();

      if (diff <= 0) {
        return '';
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const parts: string[] = [];
      if (days > 0) {
        parts.push(`${days} ${days === 1 ? "day" : "days"}`);
      }
      if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
      }
      if (minutes > 0) {
        parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      }
      if (seconds > 0 || parts.length === 0) {
        parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
      }

      return parts.join(', ');
    } catch (error) {
      console.error('[CityHallScreen] Error formatting time remaining:', error);
      return '';
    }
  };

  return {
    formatDate,
    formatTimeRemaining,
    formatElectionTimeRemaining,
    formatNextElectionTimeRemaining,
  };
}
